import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Snackbar,
  Stack,
  styled,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import UserDialog from "../../../components/Dialog/UserDialog";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Market } from "../../../model/Dto/MarketDto";
import { blue } from "@mui/material/colors";
import ItemsList from "../../../components/ItemsList";
import { StyledTable } from "../../../components/FighterCard/StyledTable";
import { Api } from "../../../request/api/api";
import { useTranslation } from "react-i18next";
import { MarketItem } from "../../../model/MarketItem";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export interface MarketDialogProps {
  open: boolean;
  fighterId: string;
  factionId: string;
  onClose: () => void;
  fetchData: () => void;
  teamId: string;
}

export default function MarketDialog({
  onClose,
  fighterId,
  factionId,
  open,
  fetchData,
  teamId,
}: MarketDialogProps) {
  const [snackbarOpen, setSnackbarOpen] = React.useState<
    "none" | "success" | "error"
  >("none");

  const { t } = useTranslation();

  const handleDialogClose = () => {
    setSnackbarOpen("none");
    onClose();
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen("none");
  };

  enum MarketType {
    tradingPost,
    factionArmoury,
    fighter,
  }

  const getMarketTypeTranslated = (marketType: MarketType) =>
    ({
      [MarketType.tradingPost]: t("fighter:marketDialog.tradingPost"),
      [MarketType.factionArmoury]: t("fighter:marketDialog.factionArmoury"),
      [MarketType.fighter]: t("fighter:marketDialog.fighter"),
    }[marketType]);

  const getMarketType = (marketType: string): MarketType => {
    if (marketType == t("fighter:marketDialog.tradingPost"))
      return MarketType.tradingPost;
    if (marketType == t("fighter:marketDialog.factionArmoury"))
      return MarketType.factionArmoury;
    return MarketType.fighter;
  };

  const [marketType, setMarketType] = useState<MarketType>(
    MarketType.tradingPost
  );

  const [loading, setLoading] = useState(false);

  const [market, setMarket] = useState<Market | undefined>(undefined);

  const [rarity, setRarity] = useState<number>(12);

  const [cash, setCash] = useState<number | undefined>(undefined);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const addEquipment = (equipmentId: string, purchaseWithCredits: boolean) => {
    setLoading(true);
    Api.fighter
      .addEquipment(fighterId, equipmentId, purchaseWithCredits)
      .then((result) => {
        if (result) {
          setLoading(false);
          setSnackbarOpen("success");
          fetchData();
          if (purchaseWithCredits) {
            UpdateTeamCash(teamId);
          }
        } else {
          setLoading(false);
          setSnackbarOpen("error");
        }
      });
  };

  const addWeapon = (weaponId: string, purchaseWithCredits: boolean) => {
    setLoading(true);
    Api.fighter
      .addWeapon(fighterId, weaponId, purchaseWithCredits)
      .then((result) => {
        if (result) {
          setLoading(false);
          setSnackbarOpen("success");
          fetchData();
          if (purchaseWithCredits) {
            UpdateTeamCash(teamId);
          }
        } else {
          setLoading(false);
          setSnackbarOpen("error");
        }
      });
  };

  const UpdateTradingPost = (factionId: string, currentRarity: number) => {
    setLoading(true);
    Api.tradingPost.getTradingPost(factionId, currentRarity).then((result) => {
      setMarket(result.market);
      setLoading(false);
    });
  };

  const UpdateTeamCash = (teamId: string) => {
    setLoading(true);
    Api.team.getTeamCash(teamId).then((result) => {
      setCash(result.cash);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (open) {
      UpdateTradingPost(factionId, rarity);
      UpdateTeamCash(teamId);
    }
  }, [open]);

  const handleRaritySliderChange = (
    _: Event,
    newValue: number | Array<number>
  ) => {
    if (typeof newValue === "number") {
      setRarity(newValue);
    }
  };

  const handleRaritySliderCommitChange = (
    _: React.SyntheticEvent | Event,
    newValue: number | Array<number>
  ) => {
    if (typeof newValue === "number") {
      setRarity(newValue);
      UpdateTradingPost(factionId, rarity);
    }
  };

  // Фильтрация данных на основе поискового запроса
  const filteredMarket = useMemo(() => {
    if (!market) {
      return undefined;
    }

    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      return market;
    }

    return {
      ...market,
      weapons: market.weapons
        .map(category => ({
          ...category,
          items: category.items.filter(item => 
            item.name.toLowerCase().includes(query)
          )
        }))
        .filter(category => category.items.length > 0),
      equipment: market.equipment
        .map(category => ({
          ...category,
          items: category.items.filter(item => 
            item.name.toLowerCase().includes(query)
          )
        }))
        .filter(category => category.items.length > 0)
    };
  }, [market, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Проверяем, есть ли результаты после фильтрации
  const hasSearchResults = useMemo(() => {
    if (!filteredMarket || !searchQuery.trim()) return true;
    
    return filteredMarket.weapons.length > 0 || filteredMarket.equipment.length > 0;
  }, [filteredMarket, searchQuery]);

  return (
    <UserDialog handleClose={handleDialogClose} open={open}>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between">
          {/*
          <FormControl variant="filled" sx={{ mt: 2, minWidth: 120 }}>
            <InputLabel>market type</InputLabel>
            <Select
              autoFocus
              value={getMarketTypeTranslated(marketType)}
              onChange={(event: SelectChangeEvent) =>
                setMarketType(getMarketType(event.target.value))
              }
              label="Faction"
              inputProps={{
                name: "faction",
                id: "faction",
              }}>
              {Object.values(MarketType)
                .filter((v) => !isNaN(Number(v)))
                .map((value) => (
                  <MenuItem
                    key={value as number}
                    value={getMarketTypeTranslated(value as number)}>
                    {getMarketTypeTranslated(value as number)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          */}
          {loading === true ? (
            <CircularProgress
              size={24}
              sx={{
                color: blue[500],
                marginLeft: "12px",
              }}
            />
          ) : (
            <></>
          )}
          {cash !== undefined ? <Chip label={`${cash} credits`} /> : <></>}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ minHeight: "200px" }}>
        <Snackbar
          open={snackbarOpen == "success"}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}>
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{ width: "100%" }}>
            Предмет успешно добавлен
          </Alert>
        </Snackbar>
        <Snackbar
          open={snackbarOpen == "error"}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}>
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            sx={{ width: "100%" }}>
            Ошибка.
          </Alert>
        </Snackbar>
        
        {/* Добавляем поле поиска вверху */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Поиск по названию..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Box sx={{ width: "100%", marginTop: 4 }}>
          <Typography gutterBottom>Редкость</Typography>
          <Slider
            aria-label="Rarity"
            getAriaValueText={(value: number) => "" + value}
            value={rarity}
            onChangeCommitted={handleRaritySliderCommitChange}
            onChange={handleRaritySliderChange}
            marks
            step={1}
            min={2}
            max={15}
            valueLabelDisplay="on"
          />
        </Box>
        {filteredMarket === undefined ? (
          <></>
        ) : (
          <>
            <MarketTable>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell align="center">Стоимость</TableCell>
                  <TableCell align="center">Редкость</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
            </MarketTable>
            
            {/* Сообщение если ничего не найдено */}
            {searchQuery.trim() && !hasSearchResults && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 4 
              }}>
                <Typography color="error">
                  По запросу "{searchQuery}" ничего не найдено
                </Typography>
              </Box>
            )}
            
            {/* Отображаем только если есть результаты или поиск неактивен */}
            {(hasSearchResults || !searchQuery.trim()) && (
              <>
                {filteredMarket.weapons.map((category, index) => (
                  <CategoryTable
                    key={index}
                    name={category.name}
                    items={category.items.map((weapon) =>
                      MarketItem.fromWeapon(weapon)
                    )}
                    onClick={addWeapon}
                  />
                ))}
                {filteredMarket.equipment.map((category, index) => (
                  <CategoryTable
                    key={index + filteredMarket.weapons.length}
                    name={category.name}
                    items={category.items.map((equipment) =>
                      MarketItem.fromEquipment(equipment)
                    )}
                    onClick={addEquipment}
                  />
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Back</Button>
      </DialogActions>
    </UserDialog>
  );
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface MarketTableProps {
  children: React.ReactNode;
}

function MarketTable({ children }: MarketTableProps) {
  return (
    <StyledTable size="small">
      <colgroup>
        <col style={{ width: "40%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "30%" }} />
      </colgroup>
      {children}
    </StyledTable>
  );
}

interface CategoryTableProps {
  name: string;
  items: MarketItem[];
  onClick: (weaponId: string, purchaseWithCredits: boolean) => void;
}

function CategoryTable({ name, items, onClick }: CategoryTableProps) {
  const [isOpen, setOpen] = useState<boolean>(false);
  
  // Не рендерим категорию если в ней нет товаров
  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <MarketTable>
        <TableBody>
          <TableRow
            onClick={() => setOpen(!isOpen)}
            sx={{ backgroundColor: blue[200], cursor: "pointer" }}>
            <TableCell colSpan={3}>{name}</TableCell>
            <TableCell>
              <IconButton aria-label="expand" size="small">
                {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </MarketTable>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <MarketTable>
          <TableBody>
            <ItemsList
              items={items}
              renderItem={(item, index) => (
                <StyledTableRow key={index}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{item.cost}</TableCell>
                  <TableCell align="center">
                    {item.rarity ?? "common"}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={2}>
                      <Button
                        onClick={() => onClick(item.id, false)}
                        style={{ backgroundColor: "rgba(200, 200, 200, 0.8)" }}
                        size="small"
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          color: "rgba(0, 0, 0, 0.87)",
                        }}>
                        Добавить
                      </Button>
                      <Button
                        onClick={() => onClick(item.id, true)}
                        style={{ backgroundColor: "rgba(200, 200, 200, 0.8)" }}
                        size="small"
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          color: "rgba(0, 0, 0, 0.87)",
                        }}>
                        Купить
                      </Button>
                    </Stack>
                  </TableCell>
                </StyledTableRow>
              )}
            />
          </TableBody>
        </MarketTable>
      </Collapse>
    </>
  );
}